import { useState, useEffect } from 'react';
import './StaffDashboard.css';

function StaffDashboard({ user, onLogout, apiBase }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // all, pending, approved, rejected
  const [selectedApp, setSelectedApp] = useState(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [message, setMessage] = useState('');

  const userRoles = user.roles || [];
  const isAdmissionsOfficer = userRoles.includes('admissions');
  const isOwner = userRoles.includes('owner');
  const isSuperAdmin = userRoles.includes('super_admin') || userRoles.includes('admin');

  useEffect(() => {
    if (isAdmissionsOfficer || isSuperAdmin) {
      fetchApplications();
    }
  }, []);

  async function fetchApplications() {
    try {
      setLoading(true);
      const response = await fetch(`${apiBase}/admin/admissions/applications`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();
      if (response.ok && data.ok) {
        setApplications(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  }

  function getFilteredApplications() {
    if (activeTab === 'all') return applications;
    if (activeTab === 'pending') {
      return applications.filter(
        (app) =>
          app.status_code === 'submitted' || app.status_code === 'in_review'
      );
    }
    if (activeTab === 'approved') {
      return applications.filter((app) => app.status_code === 'approved');
    }
    if (activeTab === 'rejected') {
      return applications.filter((app) => app.status_code === 'rejected');
    }
    return applications;
  }

  async function handleApprove(appId) {
    await handleApprovalDecision(appId, 'approve');
  }

  async function handleReject(appId) {
    await handleApprovalDecision(appId, 'reject');
  }

  async function handleApprovalDecision(appId, decision) {
    try {
      const response = await fetch(
        `${apiBase}/admin/admissions/applications/${appId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status_code: decision === 'approve' ? 'approved' : 'rejected',
            admin_note: approvalNote,
          }),
        }
      );

      const data = await response.json();
      if (response.ok && data.ok) {
        setMessage({
          type: 'success',
          text: `Aplikasi ${decision === 'approve' ? 'disetujui' : 'ditolak'}`,
        });
        setSelectedApp(null);
        setApprovalNote('');
        setTimeout(() => fetchApplications(), 500);
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Gagal memproses keputusan',
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Terjadi kesalahan',
      });
    }
  }

  function getStatusBadge(status) {
    const statuses = {
      draft: { label: 'Draft', color: 'gray' },
      submitted: { label: 'Submitted', color: 'blue' },
      in_review: { label: 'In Review', color: 'yellow' },
      approved: { label: 'Approved', color: 'green' },
      rejected: { label: 'Rejected', color: 'red' },
    };
    const st = statuses[status] || { label: status, color: 'gray' };
    return (
      <span className={`status-badge status-${st.color}`}>{st.label}</span>
    );
  }

  const filteredApps = getFilteredApplications();

  return (
    <div className="staff-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>👨‍💼 Dashboard Pengelola</h1>
          <p className="header-subtitle">Kelola pendaftaran dan laporan siswa</p>
        </div>
        <div className="header-right">
          <div className="user-info">
            <p className="user-name">{user.full_name}</p>
            <p className="user-email">{user.email}</p>
            <p className="user-roles">{userRoles.join(', ')}</p>
          </div>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {message && (
          <div className={`message-alert message-${message.type}`}>
            {message.text}
            <button
              className="close-message"
              onClick={() => setMessage('')}
            >
              ✕
            </button>
          </div>
        )}

        {/* Admissions Officer Panel */}
        {(isAdmissionsOfficer || isSuperAdmin) && (
          <section className="section">
            <div className="section-header">
              <h2>📋 Kelola Pendaftaran</h2>
            </div>

            <div className="filter-tabs">
              <button
                className={`tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                Semua ({applications.length})
              </button>
              <button
                className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
                onClick={() => setActiveTab('pending')}
              >
                Menunggu (
                {
                  applications.filter(
                    (a) =>
                      a.status_code === 'submitted' ||
                      a.status_code === 'in_review'
                  ).length
                }
                )
              </button>
              <button
                className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
                onClick={() => setActiveTab('approved')}
              >
                Disetujui (
                {applications.filter((a) => a.status_code === 'approved').length}
                )
              </button>
              <button
                className={`tab ${activeTab === 'rejected' ? 'active' : ''}`}
                onClick={() => setActiveTab('rejected')}
              >
                Ditolak (
                {applications.filter((a) => a.status_code === 'rejected').length}
                )
              </button>
            </div>

            {loading ? (
              <div className="loading">Memuat data...</div>
            ) : filteredApps.length === 0 ? (
              <div className="empty-state">
                <p>Tidak ada aplikasi untuk ditampilkan</p>
              </div>
            ) : (
              <div className="applications-table">
                <table>
                  <thead>
                    <tr>
                      <th>Nama Siswa</th>
                      <th>Nama Orang Tua</th>
                      <th>Level</th>
                      <th>Status</th>
                      <th>Tanggal Daftar</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApps.map((app) => {
                      const formData = app.form_payload || {};
                      return (
                        <tr key={app.id}>
                          <td>{formData.student_full_name || '-'}</td>
                          <td>{formData.parent_name || '-'}</td>
                          <td>{app.level_code}</td>
                          <td>{getStatusBadge(app.status_code)}</td>
                          <td>
                            {new Date(app.created_at).toLocaleDateString(
                              'id-ID'
                            )}
                          </td>
                          <td>
                            <button
                              className="btn-small btn-view"
                              onClick={() => setSelectedApp(app)}
                            >
                              Lihat & Proses
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Owner Reports Panel */}
        {(isOwner || isSuperAdmin) && (
          <section className="section">
            <h2>📊 Laporan Pendaftaran</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">
                  {applications.length}
                </div>
                <div className="stat-label">Total Pendaftaran</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {applications.filter((a) => a.status_code === 'approved')
                    .length}
                </div>
                <div className="stat-label">Diterima</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {applications.filter(
                    (a) =>
                      a.status_code === 'submitted' ||
                      a.status_code === 'in_review'
                  ).length}
                </div>
                <div className="stat-label">Menunggu Review</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">
                  {applications.filter((a) => a.status_code === 'rejected')
                    .length}
                </div>
                <div className="stat-label">Ditolak</div>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Approval Modal */}
      {selectedApp && (
        <div className="modal-overlay" onClick={() => setSelectedApp(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detail Aplikasi</h2>
              <button
                className="close-modal"
                onClick={() => setSelectedApp(null)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Data Siswa</h3>
                <div className="detail-row">
                  <span className="label">Nama Siswa:</span>
                  <span className="value">
                    {selectedApp.form_payload?.student_full_name || '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Tanggal Lahir:</span>
                  <span className="value">
                    {selectedApp.form_payload?.birth_date || '-'}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Data Orang Tua</h3>
                <div className="detail-row">
                  <span className="label">Nama:</span>
                  <span className="value">
                    {selectedApp.form_payload?.parent_name || '-'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Telepon:</span>
                  <span className="value">
                    {selectedApp.form_payload?.phone || '-'}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h3>Status Aplikasi</h3>
                <div className="detail-row">
                  <span className="label">Level:</span>
                  <span className="value">{selectedApp.level_code}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className="value">
                    {getStatusBadge(selectedApp.status_code)}
                  </span>
                </div>
              </div>

              {(selectedApp.status_code === 'submitted' ||
                selectedApp.status_code === 'in_review') && (
                <div className="approval-section">
                  <h3>Keputusan</h3>
                  <div className="form-group">
                    <label>Catatan (opsional)</label>
                    <textarea
                      value={approvalNote}
                      onChange={(e) => setApprovalNote(e.target.value)}
                      placeholder="Masukkan catatan untuk orang tua..."
                      rows="3"
                    />
                  </div>

                  <div className="button-group">
                    <button
                      className="btn-approve"
                      onClick={() => handleApprove(selectedApp.id)}
                    >
                      ✓ Setujui
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleReject(selectedApp.id)}
                    >
                      ✗ Tolak
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffDashboard;
